import React from "react";
import {
  MODULES_ALIAS,
  CONTENT_ELEMENTS_ALIAS,
  EVENT_LIST_CONTENT_ELEMENT_ALIAS,
} from "./aliases";
import { Hero } from "@modules/Hero/Hero";
import { Container } from "@modules/Container/Container";
import { HeroModule } from "./types/modules/Hero";
import { ContainerModule } from "./types/modules/Container";
import { CardList } from "@modules/CardList/CardList";
import { CardListModule } from "./types/modules/CardList";
import { EventList, EventListProps } from "@modules/EventList/EventList";
import { FaqList } from "@modules/FaqList/FaqList";
import { FaqListModule } from "./types/modules/FaqList";
import { MediaModule } from "./types/modules/Media";
import { Media } from "@modules/Media/Media";
import { MediaSliderModule } from "./types/modules/MediaSlider";
import { MediaSlider } from "@modules/MediaSlider/MediaSlider";
import { Text, TextModuleProps } from "@modules/Text/Text";
import { TableModule } from "./types/modules/Table";
import { Table } from "@modules/Table/Table";
import { VideoEmbed } from "@modules/VideoEmbed/VideoEmbed";
import { VideoEmbedModule } from "./types/modules/VideoEmbed";
import { GoogleMap, GoogleMapProps } from "@modules/GoogleMap/GoogleMap";
import { Accordion, AccordionProps } from "@modules/Accordion/Accordion";
import { BlockquoteModule } from "./types/modules/Blockquote";
import { Blockquote } from "@modules/Blockquote/Blockquote";
import {
  ContactInformation,
  ContactInformationListProps,
} from "@modules/ContactInformation/ContactInformation";
import { Icons } from "@modules/Icons/Icons";
import { IconModule } from "./types/modules/Icon";
import {
  VideoCard,
  VideoCardProps,
} from "@shared-components/Card/VideoCard/VideoCard";
import {
  EventCard,
  EventCardProps,
} from "@shared-components/Card/EventCard/EventCard";
import {
  ImageCard,
  ImageCardProps,
} from "@shared-components/Card/ImageCard/ImageCard";
import { Download, DownloadProps } from "@modules/Download/Download";
import { SHORT_CODES_MAP } from "./shortcodes-map";
import { HTMLEmbed, HTMLEmbedProps } from "./HTMLEmbed";
import { FAQ } from "@modules/FAQ/FAQ";
import { FaqModule } from "./types/modules/Faq";
import { StorySlider } from "@modules/StorySlider/StorySlider";
import { StorySliderModule } from "./types/modules/StorySlider";
import { SmallHero } from "@modules/SmallHero/SmallHero";
import { SmallHeroModule } from "./types/modules/SmallHero";
import { MyBottleCollection } from "@modules/MyBottleCollection/MyBottleCollection";
import { BottleDetails } from "@modules/BottleDetails/BottleDetails";
import { Kiosk } from "@modules/Kiosk/Kiosk";
import { EmbedNewsletter } from "@modules/EmbedNewsletter/EmbedNewsletter";
import { EmbedEmail } from "@modules/EmbedEmail/EmbedEmail";
import { EmbedEmailModule } from "./types/modules/EmbedEmail";
import { NPSScale } from "@modules/NPSScale/NPSScale";

export interface ModuleProps
  extends React.PropsWithoutRef<Record<string, unknown>> {
  cbKey: string | number;
  shortCodes: string[];
}

export interface loadContentModuleArgs {
  alias: string;
  props: ModuleProps;
}

/**
 * Carlsberg Headless modules (and pages, sites, etc.) has a `key` property with
 * a UID for the given resource in Umbraco.
 *
 * But React does not let us access `props.key` in our components as this is a React internal property...
 *
 * Components can access the Umbraco UID via `props.cbKey`.
 * @param props
 */
const internalizeExternalKeyProps = (props: Record<string, unknown>) => {
  const _props = { ...props };
  if (props.hasOwnProperty("key")) {
    _props["cbKey"] = props.key;
  }
  return _props;
};

export const getReactCMSComponent = ({
  alias,
  props,
}: loadContentModuleArgs): React.ReactElement => {
  if (props.shortCodes && props.shortCodes.length > 0) {
    const embeddableShortCode = props.shortCodes.find((shortCode) => {
      return Object.keys(SHORT_CODES_MAP.EMBEDS).includes(shortCode);
    });

    if (embeddableShortCode === "my_bottle_collection_module") {
      return React.createElement(MyBottleCollection);
    }

    if (embeddableShortCode) {
      return React.createElement(
        HTMLEmbed,
        internalizeExternalKeyProps({
          ...SHORT_CODES_MAP.EMBEDS[embeddableShortCode],
          ...props, // props from whatever module that caused the embed shortcode to get rendered
        }) as unknown as HTMLEmbedProps
      );
    }
  }

  const shortCodeIsBottleCollection = props.shortCodes.find(
    (shortCode) => shortCode === "my_bottle_collection_module"
  );

  const shortCodeBottleCollectionKiosk = props.shortCodes.find(
    (shortCode) => shortCode === "bottle_collection_kiosk"
  );

  const shortCodeBottleCollectionDetail = props.shortCodes.find(
    (shortCode) => shortCode === "bottle_collection_single_bottle"
  );

  const shortCodeNewsletter = props.shortCodes.find(
    (shortCode) => shortCode === "newsletter_module"
  );

  const shortCodeNPS = props.shortCodes.find(
    (shortCode) => shortCode === "NPS_module"
  );

  if (shortCodeIsBottleCollection) {
    return React.createElement(MyBottleCollection);
  }

  if (shortCodeBottleCollectionDetail) {
    return React.createElement(BottleDetails);
  }

  if (shortCodeBottleCollectionKiosk) {
    return React.createElement(Kiosk);
  }

  if (shortCodeNewsletter) {
    return React.createElement(EmbedNewsletter);
  }

  if (shortCodeNPS) {
    return React.createElement(NPSScale);
  }

  switch (alias) {
    case MODULES_ALIAS.containerModule:
    case MODULES_ALIAS.containerModuleElement:
      return React.createElement(
        Container,
        internalizeExternalKeyProps(props) as unknown as ContainerModule
      );

    case MODULES_ALIAS.heroModule:
    case MODULES_ALIAS.heroModuleElement:
      return React.createElement(
        Hero,
        internalizeExternalKeyProps(props) as unknown as HeroModule
      );

    case MODULES_ALIAS.mapModule:
    case MODULES_ALIAS.mapModuleElement:
      return React.createElement(
        GoogleMap,
        internalizeExternalKeyProps(props) as unknown as GoogleMapProps
      );

    case MODULES_ALIAS.cardListModule:
    case MODULES_ALIAS.cardListElement:
    case MODULES_ALIAS.cardListModuleElement:
      return React.createElement(
        CardList,
        internalizeExternalKeyProps(props) as unknown as CardListModule
      );

    case MODULES_ALIAS.eventListModule:
    case MODULES_ALIAS.eventListModuleElement:
    case MODULES_ALIAS.eventsListModule:
    case MODULES_ALIAS.eventsListModuleElement:
      return React.createElement(
        EventList,
        internalizeExternalKeyProps(props) as unknown as EventListProps
      );

    case MODULES_ALIAS.faqListElement:
    case MODULES_ALIAS.faqListModule:
    case MODULES_ALIAS.faqListModuleElement:
      return React.createElement(
        FaqList,
        internalizeExternalKeyProps(props) as unknown as FaqListModule
      );

    case MODULES_ALIAS.faqModuleElement:
    case MODULES_ALIAS.faqModule:
      return React.createElement(
        FAQ,
        internalizeExternalKeyProps(props) as unknown as FaqModule
      );

    case MODULES_ALIAS.textModule:
    case MODULES_ALIAS.textModuleElement:
      return React.createElement(
        Text,
        internalizeExternalKeyProps(props) as unknown as TextModuleProps
      );

    case MODULES_ALIAS.mediaModule:
    case MODULES_ALIAS.mediaModuleElement:
      return React.createElement(
        Media,
        internalizeExternalKeyProps(props) as unknown as MediaModule
      );

    case MODULES_ALIAS.mediaSliderModule:
    case MODULES_ALIAS.mediaSliderModuleElement:
      return React.createElement(
        MediaSlider,
        internalizeExternalKeyProps(props) as unknown as MediaSliderModule
      );

    case CONTENT_ELEMENTS_ALIAS.imageCard:
    case CONTENT_ELEMENTS_ALIAS.imageCardElement:
      return React.createElement(
        ImageCard,
        internalizeExternalKeyProps(props) as unknown as ImageCardProps
      );

    case CONTENT_ELEMENTS_ALIAS.videoCard:
    case CONTENT_ELEMENTS_ALIAS.videoCardElement:
      return React.createElement(
        VideoCard,
        internalizeExternalKeyProps(props) as unknown as VideoCardProps
      );

    case EVENT_LIST_CONTENT_ELEMENT_ALIAS.event:
    case EVENT_LIST_CONTENT_ELEMENT_ALIAS.eventElement:
      return React.createElement(
        EventCard,
        internalizeExternalKeyProps(props) as unknown as EventCardProps
      );

    case MODULES_ALIAS.tableModule:
    case MODULES_ALIAS.tableModuleElement:
      return React.createElement(
        Table,
        internalizeExternalKeyProps(props) as unknown as TableModule
      );

    case MODULES_ALIAS.iconModule:
    case MODULES_ALIAS.iconModuleElement:
      return React.createElement(
        Icons,
        internalizeExternalKeyProps(props) as unknown as IconModule
      );

    case MODULES_ALIAS.accordionListModule:
    case MODULES_ALIAS.accordionListModuleElement:
      return React.createElement(
        Accordion,
        internalizeExternalKeyProps(props) as unknown as AccordionProps
      );

    case MODULES_ALIAS.blockquoteModule:
    case MODULES_ALIAS.blockquoteModuleElement:
      return React.createElement(
        Blockquote,
        internalizeExternalKeyProps(props) as unknown as BlockquoteModule
      );

    case MODULES_ALIAS.contactModule:
    case MODULES_ALIAS.contactModuleElement:
      return React.createElement(
        ContactInformation,
        internalizeExternalKeyProps(
          props
        ) as unknown as ContactInformationListProps
      );

    case MODULES_ALIAS.videoEmbedModule:
    case MODULES_ALIAS.videoEmbedElement:
      return React.createElement(
        VideoEmbed,
        internalizeExternalKeyProps(props) as unknown as VideoEmbedModule
      );

    case MODULES_ALIAS.downloadModule:
    case MODULES_ALIAS.downloadModuleElement:
      return React.createElement(
        Download,
        internalizeExternalKeyProps(props) as unknown as DownloadProps
      );

    case MODULES_ALIAS.storySliderModule:
    case MODULES_ALIAS.storySliderModuleElement:
      return React.createElement(
        StorySlider,
        internalizeExternalKeyProps(props) as unknown as StorySliderModule
      );

    case MODULES_ALIAS.smallHeroModule:
    case MODULES_ALIAS.smallHeroModuleElement:
      return React.createElement(
        SmallHero,
        internalizeExternalKeyProps(props) as unknown as SmallHeroModule
      );
    case MODULES_ALIAS.emailModule:
    case MODULES_ALIAS.emailModuleElement:
      return React.createElement(
        EmbedEmail,
        internalizeExternalKeyProps(props) as unknown as EmbedEmailModule
      );

    default:
      return React.createElement(
        "p",
        { key: Math.random() * 1000 },
        `Unknown module: ${String(alias)}`
      );
  }
};
